import { z } from "zod";
import { discardSiteChangeResult, getJobResult, publishSiteChangeResult, unreleasedChangeSchema } from "./mcp/schemas";

/**
 * The `/v1` family of `@sitebrew/contracts` (spec #316, ticket #321, D11/D43).
 *
 * Every response the API composes under `/v1`, written **from the route
 * handlers** in `packages/workers/api/src/index.ts` and
 * `actions-oidc-routes.ts` — the `public*` projections and the literal bodies
 * — not from what any consumer believed it was reading. The #289 inventory
 * (rows 28–45) is the map of who reads what; the table in `v1Responses`
 * below is the one the API validates its own answers against.
 *
 * Validation policy (#290): the API `safeParse`s its own output and logs a
 * mismatch (`CONTRACTS_STRICT=1` makes it fail); the console `parse`s every
 * answer and fails closed. Both ends read these same objects.
 *
 * Objects strip unknown keys rather than refusing them, on purpose: a field
 * the API starts sending before the console knows it is the *expand* half of
 * expand/contract, and must reach an older console as a non-event. A field
 * the API stops sending, or renames, is the failure this family exists to
 * catch, and it fails on both ends.
 */

/** Bumped only for a breaking change, which arrives by expand/contract (#290 §5, `docs/contracts.md`). */
export const version = 1 as const;

/* ---------------------------------------------------------------- errors */

/**
 * One error shape for the whole surface (design note 0001 §6), produced by
 * the API, by the console Worker's own refusals (`refuse()`), and read by the
 * app (`lib/api.ts`). Inventory row 28 — the console Worker's `notFound` was
 * the one producer answering a flat shape, and it is this schema now.
 */
export const errorEnvelope = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelope>;

/** The envelope as a value — the one way a producer composes it. */
export const errorBody = (code: string, message: string): ErrorEnvelope => ({
  error: { code, message },
});

/* ------------------------------------------------------------ primitives */

/**
 * The shape of a slug, and only the shape: it becomes a DNS label. Whether a
 * label is reserved is `/naming`'s `isReservedLabel`; whether it is taken or
 * retired is the registry's to answer at runtime (inventory row 31).
 */
export const SLUG_SHAPE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
export const siteSlug = z.string().regex(SLUG_SHAPE);

/** A name on a site or a workspace: 1–200 characters (`NAME_MAX_LENGTH`, row 32). */
export const NAME_MAX_LENGTH = 200;
export const entityName = z.string().min(1).max(NAME_MAX_LENGTH);

/** Epoch milliseconds, which is how every `*At` the API composes itself is sent. */
const epochMs = z.number().int();

export const membershipRole = z.enum(["owner", "member"]);
export type MembershipRole = z.infer<typeof membershipRole>;

export const siteStatus = z.enum(["creating", "ready", "suspended"]);
export type SiteStatus = z.infer<typeof siteStatus>;

/* ------------------------------------------------------- /me, workspaces */

/** One workspace the caller belongs to (`listWorkspaces`, `WorkspaceRow`). */
export const membership = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  role: membershipRole,
});
export type Membership = z.infer<typeof membership>;

/** `GET /v1/me` (row 29). `name` and `emailVerified` are Better Auth's user columns, always present. */
export const meResponse = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    emailVerified: z.boolean(),
  }),
  workspaces: z.array(membership),
});
export type Me = z.infer<typeof meResponse>;

/** `POST /v1/workspaces` → `201` (row 36). */
export const workspaceCreatedResponse = z.object({ workspace: membership });

/* ------------------------------------------------------------------ sites */

/** `publicSite` (row 30): `repo` is `repoFullName`, `null` until the repository exists. */
export const site = z.object({
  id: z.string(),
  slug: siteSlug,
  name: z.string(),
  status: siteStatus,
  hostname: z.string().nullable(),
  repo: z.string().nullable(),
  workspaceId: z.string(),
  createdAt: epochMs,
});
export type Site = z.infer<typeof site>;

/** `GET /v1/workspaces/:ws/sites` (row 33) — ordered `createdAt` ascending by the API. */
export const sitesResponse = z.object({ sites: z.array(site) });
/** `POST …/sites` → `201`, `GET /v1/sites/:site`, `PATCH /v1/sites/:site` (row 33). */
export const siteResponse = z.object({ site });

/** Session-authenticated site activity. Each job uses get_job's additive progress fields. */
export const siteActivityResponse = z.object({
  jobs: z.array(z.object({ request: z.string(), job: getJobResult })),
  changes: z.array(z.object({ jobId: z.string(), change: unreleasedChangeSchema, publishable: z.boolean(), discardable: z.boolean() })),
});
/** Console-only session routes; the MCP tools share their core operations. */
export const publishChangeResponse = publishSiteChangeResult;
export const discardChangeResponse = discardSiteChangeResult;

/* ----------------------------------------------------------------- tokens */

/** `publicToken` (row 34): §7's exact key set; `scopes` deliberately absent. */
export const token = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  createdBy: z.string(),
  createdAt: epochMs,
  lastUsedAt: epochMs.nullable(),
  /** `null` means the key never expires (row 35). */
  expiresAt: epochMs.nullable(),
  revokedAt: epochMs.nullable(),
});
export type Token = z.infer<typeof token>;

/** The answer to `POST`, the only place the value exists. */
export const issuedToken = token.extend({ value: z.string() });
export type IssuedToken = z.infer<typeof issuedToken>;

export const tokensResponse = z.object({ tokens: z.array(token) });
export const tokenIssuedResponse = z.object({ token: issuedToken });

/* ---------------------------------------------------- members and invites */

/** `publicMember` (row 37). */
export const member = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string(),
  role: membershipRole,
  createdAt: epochMs,
});
export type Member = z.infer<typeof member>;

export const membersResponse = z.object({ members: z.array(member) });

/** `publicInvite` (row 38): never the token. */
export const invite = z.object({
  id: z.string(),
  email: z.string(),
  role: membershipRole,
  invitedBy: z.string(),
  createdAt: epochMs,
  expiresAt: epochMs,
});
export type Invite = z.infer<typeof invite>;

export const invitesResponse = z.object({ invites: z.array(invite) });
export const inviteResponse = z.object({ invite });

/** `publicPendingInvite` (row 38): no `email`, the caller already knows it is theirs. */
export const pendingInvite = z.object({
  id: z.string(),
  workspaceId: z.string(),
  workspaceName: z.string(),
  role: membershipRole,
  expiresAt: epochMs,
});
export type PendingInvite = z.infer<typeof pendingInvite>;

export const pendingInvitesResponse = z.object({ invites: z.array(pendingInvite) });

/** `POST /v1/invites/accept` and `POST /v1/invites/mine/:invite/accept` (row 38). */
export const inviteAcceptedResponse = z.object({ workspaceId: z.string() });

/* ------------------------------------------------- connected applications */

/** `publicConnectedApplication` (row 39): `connectedAt` normalised to ISO 8601 by the API. */
export const connectedApplication = z.object({
  clientId: z.string(),
  name: z.string().nullable(),
  scopes: z.array(z.string()),
  connectedAt: z.iso.datetime(),
});
export type ConnectedApplication = z.infer<typeof connectedApplication>;

export const connectedApplicationsResponse = z.object({
  applications: z.array(connectedApplication),
});

/* -------------------------------------------------------- oauth, uploads */

/** `POST /v1/oauth/select-workspace` (row 40). */
export const selectWorkspaceResponse = z.object({ ok: z.literal(true) });

/** `POST /v1/workspaces/:ws/uploads` → `201` (`requestUpload`; `expiresAt` is ISO 8601 here). */
export const uploadRequestedResponse = z.object({
  uploadId: z.string(),
  uploadUrl: z.string(),
  expiresAt: z.iso.datetime(),
});

/* ------------------------------------------------- /v1/actions (row 45) */

/** `POST /v1/actions/upload-token` (`MintedR2Credentials` + the echoed prefix). */
export const actionsUploadTokenResponse = z.object({
  accessKeyId: z.string(),
  secretAccessKey: z.string(),
  sessionToken: z.string(),
  expiresAt: epochMs,
  accountId: z.string(),
  bucket: z.string(),
  prefix: z.string(),
});
/** `POST /v1/actions/canary-token` (`repositoryToken`). */
export const actionsCanaryTokenResponse = z.object({ token: z.string(), expiresAt: epochMs });
/** `POST /v1/actions/canary-status` (`CanaryRunConclusion`). */
export const actionsCanaryStatusResponse = z.object({
  conclusion: z.enum(["pending", "success", "failure"]),
});
/** `POST /v1/actions/deploy-callback`. */
export const actionsDeployCallbackResponse = z.object({ hostname: z.string(), prefix: z.string() });
/** `DELETE /v1/actions/deploy-callback`. */
export const actionsDeployRemovedResponse = z.object({ hostname: z.string(), removed: z.literal(true) });
/** `POST /v1/actions/artifact-cleanup`. */
export const actionsArtifactCleanupResponse = z.object({ deleted: z.boolean() });
/** `POST /v1/actions/publish-callback`. */
export const actionsPublishCallbackResponse = z.object({ received: z.literal(true) });

/* ----------------------------------------------- answered by Better Auth */

/**
 * `/v1/auth/*` is the library's surface, not a body the API composes, so the
 * API does not validate it and these stay loose: the fields the console
 * reads, read from `@better-auth/oauth-provider@1.7.1`'s own implementation
 * (row 40; `screens/consent.tsx` on why not its documentation). Anything else
 * the library sends passes through.
 */
export const publicClientResponse = z.looseObject({
  name: z.string().optional(),
  uri: z.string().optional(),
});
/** `/oauth2/consent` and `/oauth2/continue` answer alike. */
export const oauthResumeResponse = z.looseObject({
  redirect: z.boolean().optional(),
  url: z.string().optional(),
});
/** `POST /v1/auth/sign-in/magic-link`. */
export const magicLinkRequestedResponse = z.looseObject({ status: z.boolean().optional() });
/** `POST /v1/auth/sign-in/social` with `disableRedirect`. */
export const socialSignInResponse = z.looseObject({
  url: z.string().optional(),
  redirect: z.boolean().optional(),
});
/** `POST /v1/auth/sign-out`: nothing is read from it. */
export const signOutResponse = z.unknown();

/* --------------------------------------------------------------- 204s */

/** A `204`: the archive, revoke, remove and disconnect answers. */
export const noContent = z.undefined();

/* --------------------------------------------------------------- registry */

/**
 * Route → the schema its `2xx` answer must parse against, keyed by Hono's
 * registered pattern (`"GET /v1/workspaces/:ws/sites"`). The API's response
 * middleware looks its route up here; a `/v1` route the API composes and this
 * table does not name is a test failure there, not a silent gap.
 *
 * Not listed on purpose: `/v1/auth/*` (the library's) and `/v1/jobs/*`
 * (the executor handover).
 */
export const v1Responses = {
  "GET /v1/me": meResponse,
  "POST /v1/workspaces": workspaceCreatedResponse,
  "GET /v1/workspaces/:ws/sites": sitesResponse,
  "POST /v1/workspaces/:ws/sites": siteResponse,
  "GET /v1/sites/:site": siteResponse,
  "GET /v1/sites/:site/activity": siteActivityResponse,
  "POST /v1/sites/:site/changes/:change/publish": publishChangeResponse,
  "DELETE /v1/sites/:site/changes/:change": discardChangeResponse,
  "PATCH /v1/sites/:site": siteResponse,
  "DELETE /v1/sites/:site": noContent,
  "GET /v1/workspaces/:ws/tokens": tokensResponse,
  "POST /v1/workspaces/:ws/tokens": tokenIssuedResponse,
  "DELETE /v1/workspaces/:ws/tokens/:token": noContent,
  "GET /v1/workspaces/:ws/members": membersResponse,
  "DELETE /v1/workspaces/:ws/members/:userId": noContent,
  "GET /v1/workspaces/:ws/invites": invitesResponse,
  "POST /v1/workspaces/:ws/invites": inviteResponse,
  "DELETE /v1/workspaces/:ws/invites/:invite": noContent,
  "POST /v1/invites/accept": inviteAcceptedResponse,
  "GET /v1/invites/mine": pendingInvitesResponse,
  "POST /v1/invites/mine/:invite/accept": inviteAcceptedResponse,
  "POST /v1/workspaces/:ws/uploads": uploadRequestedResponse,
  "GET /v1/connected-applications": connectedApplicationsResponse,
  "DELETE /v1/connected-applications": noContent,
  "POST /v1/oauth/select-workspace": selectWorkspaceResponse,
  "POST /v1/actions/upload-token": actionsUploadTokenResponse,
  "POST /v1/actions/canary-token": actionsCanaryTokenResponse,
  "POST /v1/actions/canary-status": actionsCanaryStatusResponse,
  "POST /v1/actions/deploy-callback": actionsDeployCallbackResponse,
  "DELETE /v1/actions/deploy-callback": actionsDeployRemovedResponse,
  "POST /v1/actions/artifact-cleanup": actionsArtifactCleanupResponse,
  "POST /v1/actions/publish-callback": actionsPublishCallbackResponse,
} as const satisfies Record<string, z.ZodType>;
export type V1Route = keyof typeof v1Responses;
